// Claim Intelligence Package™ — shared visual primitives
// Consulting-deliverable design system used across every section of the
// package. Real data only; every component supports a professional empty
// state via the `hint`/`empty` props of its container.

import type { ReactNode } from "react";
import { ShieldCheck } from "lucide-react";

/* ─────────────────────────────────────────────────────────── */
/* Page frame                                                  */
/* ─────────────────────────────────────────────────────────── */

/**
 * PackagePage — the canonical section-page frame.
 * Renders a numbered eyebrow ("SECTION 04"), display-serif title, an
 * optional subtitle, a thin gold rule, and generous consulting-report
 * padding. Every top-level report section uses this so pages feel like
 * chapters of one bound deliverable, not a scrolling web page.
 */
export function PackagePage({
  id,
  number,
  eyebrow = "Section",
  title,
  subtitle,
  aside,
  children,
  tone = "light",
  compact = false,
  bare = false,
}: {
  id?: string;
  number?: string;
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  tone?: "light" | "dark";
  compact?: boolean;
  /** When true, suppress the eyebrow/number/title/subtitle/rule and decorative
   *  wrapper — used to eliminate first-section titles that merely restate the
   *  chapter header above. The section wrapper (with id) is preserved so
   *  anchors, TOC links, and PDF bookmarks continue to resolve. */
  bare?: boolean;
}) {
  const isDark = tone === "dark";
  if (bare) {
    return (
      <section
        id={id ?? (number ? `section-${number}` : undefined)}
        className="scroll-mt-24 report-rise print-avoid-break"
      >
        {children}
      </section>
    );
  }
  return (
    <section
      id={id ?? (number ? `section-${number}` : undefined)}
      className={[
        "scroll-mt-24 report-rise print-avoid-break",
        "relative overflow-hidden rounded-3xl border shadow-card",
        compact ? "px-6 py-8 md:px-10 md:py-10" : "px-6 py-10 md:px-14 md:py-14",
        isDark
          ? "border-white/10 bg-navy text-navy-foreground shadow-elevated"
          : "border-border bg-card",
      ].join(" ")}
    >

      {/* Corner ornament */}
      <span
        aria-hidden
        className={[
          "pointer-events-none absolute right-8 top-8 hidden h-8 w-8 md:block",
          isDark ? "opacity-40" : "opacity-70",
        ].join(" ")}
      >
        <svg viewBox="0 0 32 32" fill="none" className="h-full w-full">
          <path
            d="M0 0h12v1.5H1.5V12H0z"
            fill={isDark ? "rgba(255,255,255,0.35)" : "var(--color-fasttrack)"}
            transform="translate(20 0)"
          />
        </svg>
      </span>
      <span
        aria-hidden
        className={[
          "pointer-events-none absolute bottom-8 left-8 hidden h-8 w-8 md:block",
          isDark ? "opacity-40" : "opacity-70",
        ].join(" ")}
      >
        <svg viewBox="0 0 32 32" fill="none" className="h-full w-full">
          <path
            d="M0 20h1.5V30.5H12V32H0z"
            fill={isDark ? "rgba(255,255,255,0.35)" : "var(--color-fasttrack)"}
          />
        </svg>
      </span>

      <header
        className={[
          "flex flex-col gap-6 md:flex-row md:items-end md:justify-between",
        ].join(" ")}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            {number && (
              <span
                className={[
                  "section-eyebrow font-mono text-[10px] font-medium uppercase tracking-[0.24em]",
                  isDark ? "text-fasttrack" : "text-fasttrack",
                ].join(" ")}
              >
                {eyebrow} {number}
              </span>
            )}
            <span
              className={[
                "section-divider h-px flex-1 max-w-[120px]",
                isDark ? "bg-fasttrack/40" : "bg-fasttrack/50",
              ].join(" ")}
            />
          </div>
          <h2
            className={[
              "section-title package-title mt-3 font-display text-[34px] leading-[1.05] md:text-[46px]",
              isDark ? "text-white" : "text-navy",
            ].join(" ")}
          >
            {title}
          </h2>
          {subtitle && (
            <p
              className={[
                "section-subtitle mt-2 max-w-2xl text-[14px] leading-relaxed",
                isDark ? "text-white/70" : "text-muted-foreground",
              ].join(" ")}
            >
              {subtitle}
            </p>
          )}
        </div>
        {aside && <div className="shrink-0">{aside}</div>}
      </header>

      <div
        className={[
          "section-rule mt-6 h-px w-full",
          isDark ? "bg-white/10" : "bg-border",
        ].join(" ")}
      />

      <div className="mt-8">{children}</div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Certification seal                                          */
/* ─────────────────────────────────────────────────────────── */

/**
 * SealMark — concentric-ring certification seal. Purely decorative;
 * consumers pass real label text (e.g. "Certified by ClaimGuard").
 */
export function SealMark({
  label = "Certified · ClaimGuard",
  size = 96,
  tone = "gold",
}: {
  label?: string;
  size?: number;
  tone?: "gold" | "navy" | "ivory";
}) {
  const stroke =
    tone === "navy"
      ? "var(--color-navy)"
      : tone === "ivory"
      ? "rgba(255,255,255,0.85)"
      : "var(--color-fasttrack)";
  const text = tone === "ivory" ? "rgba(255,255,255,0.9)" : "var(--color-navy)";
  const uid = `seal-${label.replace(/[^a-z0-9]/gi, "")}-${size}`;
  const r = size / 2 - 6;
  return (
    <div
      aria-hidden
      className="relative grid place-items-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <path
            id={uid}
            d={`M ${size / 2},${size / 2} m -${r - 8},0 a ${r - 8},${r - 8} 0 1,1 ${(r - 8) * 2},0 a ${r - 8},${r - 8} 0 1,1 -${(r - 8) * 2},0`}
          />
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth={1.2}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r - 4}
          fill="none"
          stroke={stroke}
          strokeWidth={0.6}
          opacity={0.6}
        />
        <text
          fill={text}
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 8,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          <textPath href={`#${uid}`} startOffset="0%">
            {label} · {label} ·{" "}
          </textPath>
        </text>
      </svg>
      <div className="absolute grid place-items-center">
        <ShieldCheck
          className="h-6 w-6"
          style={{ color: tone === "ivory" ? "#fff" : "var(--color-navy)" }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Stat tile                                                   */
/* ─────────────────────────────────────────────────────────── */

export function StatTile({
  label,
  value,
  hint,
  icon: Icon,
  accent = "navy",
  size = "md",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  accent?: "navy" | "savings" | "trust" | "warn" | "danger" | "gold";
  size?: "md" | "lg";
}) {
  const accentCls =
    accent === "savings"
      ? "text-savings"
      : accent === "trust"
      ? "text-trust"
      : accent === "warn"
      ? "text-warn"
      : accent === "danger"
      ? "text-destructive"
      : accent === "gold"
      ? "text-fasttrack"
      : "text-navy";
  const iconBg =
    accent === "savings"
      ? "bg-savings-soft text-savings"
      : accent === "trust"
      ? "bg-trust-soft text-trust"
      : accent === "warn"
      ? "bg-fasttrack-soft text-warn"
      : accent === "danger"
      ? "bg-destructive/10 text-destructive"
      : accent === "gold"
      ? "bg-fasttrack-soft text-fasttrack"
      : "bg-secondary text-navy";
  return (
    <div className="relative flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </div>
        {Icon && (
          <div className={["grid h-9 w-9 place-items-center rounded-lg", iconBg].join(" ")}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div
        className={[
          "mt-3 font-display leading-none tabular-nums",
          size === "lg" ? "text-4xl md:text-5xl" : "text-3xl md:text-4xl",
          accentCls,
        ].join(" ")}
      >
        {value}
      </div>
      {hint && (
        <div className="mt-2 text-[12px] leading-snug text-muted-foreground">
          {hint}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Confidence gauge (semicircle)                               */
/* ─────────────────────────────────────────────────────────── */

export function ConfidenceGauge({
  value,
  label,
  size = 180,
  caption,
}: {
  value: number; // 0..100
  /** Required: a bare "Confidence" label is not permitted (SAB-004). */
  label: string;
  size?: number;
  caption?: string;
}) {
  const v = Math.max(0, Math.min(100, value));
  const stroke = 12;
  const r = size / 2 - stroke;
  const cx = size / 2;
  const cy = size / 2;
  const circ = Math.PI * r;
  const dash = (v / 100) * circ;
  const color =
    v >= 85
      ? "var(--color-savings)"
      : v >= 65
      ? "var(--color-trust)"
      : v >= 45
      ? "var(--color-fasttrack)"
      : "var(--color-destructive)";
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 12} viewBox={`0 0 ${size} ${size / 2 + 12}`}>
        <path
          d={`M ${stroke} ${cy} A ${r} ${r} 0 0 1 ${size - stroke} ${cy}`}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <path
          d={`M ${stroke} ${cy} A ${r} ${r} 0 0 1 ${size - stroke} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
        />
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: size * 0.24,
            fill: "var(--color-navy)",
          }}
        >
          {v}%
        </text>
      </svg>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
      {caption && (
        <div className="mt-1 text-[11px] text-muted-foreground">{caption}</div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Donut chart (severity distribution etc.)                    */
/* ─────────────────────────────────────────────────────────── */

export function DonutRing({
  segments,
  size = 180,
  centerLabel,
  centerValue,
}: {
  segments: { label: string; value: number; color: string }[];
  size?: number;
  centerLabel?: string;
  centerValue?: ReactNode;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const stroke = 22;
  const r = size / 2 - stroke;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="flex items-center gap-6">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth={stroke}
        />
        {total > 0 &&
          segments.map((s, i) => {
            const len = (s.value / total) * c;
            const el = (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += len;
            return el;
          })}
        <text
          x={size / 2}
          y={size / 2 - 2}
          textAnchor="middle"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: size * 0.22,
            fill: "var(--color-navy)",
          }}
        >
          {centerValue ?? total}
        </text>
        {centerLabel && (
          <text
            x={size / 2}
            y={size / 2 + 18}
            textAnchor="middle"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fill: "var(--color-muted-foreground)",
              fontWeight: 600,
            }}
          >
            {centerLabel}
          </text>
        )}
      </svg>
      <ul className="space-y-2 text-sm">
        {segments.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: s.color }}
            />
            <span className="min-w-24 text-navy">{s.label}</span>
            <span className="ml-auto tabular-nums font-semibold text-navy">
              {s.value}
            </span>
          </li>
        ))}
        {total === 0 && (
          <li className="text-muted-foreground">No data available</li>
        )}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Gold rule / eyebrow                                         */
/* ─────────────────────────────────────────────────────────── */

export function GoldRule({ width = 48 }: { width?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block h-[2px] bg-fasttrack"
      style={{ width }}
    />
  );
}

export function Eyebrow({
  children,
  tone = "navy",
}: {
  children: ReactNode;
  tone?: "navy" | "gold" | "ivory";
}) {
  const cls =
    tone === "gold"
      ? "text-fasttrack"
      : tone === "ivory"
      ? "text-white/70"
      : "text-trust";
  return (
    <span
      className={[
        "font-mono text-[11px] font-semibold uppercase tracking-[0.28em]",
        cls,
      ].join(" ")}
    >
      {children}
    </span>
  );
}
